import { useState, useEffect } from 'react';
import { Board } from '../../types';
import * as BoardService from '../../services/boardService';

export function useAppRouting(
    boards: Board[],
    setBoards: React.Dispatch<React.SetStateAction<Board[]>>,
    isLoading: boolean
) {
    const [viewToken, setViewToken] = useState<string | null>(null);
    const [contentPath, setContentPath] = useState<string | null>(null);
    const [activeBoardId, setActiveBoardId] = useState<string | null>(null);
    const [boardNotFound, setBoardNotFound] = useState(false);
    const [isRestoringUrl, setIsRestoringUrl] = useState(true);

    const [isHelpPage, setIsHelpPage] = useState(window.location.pathname.startsWith('/help'));
    const [activeHelpArticleId, setActiveHelpArticleId] = useState<string | null>(() => {
        const match = window.location.pathname.match(/^\/help\/([a-z0-9-]+)$/i);
        return match ? match[1] : null;
    });
    const [isPrivacyPage, setIsPrivacyPage] = useState(window.location.pathname === '/privacy');

    // Initial Route Check
    useEffect(() => {
        const pathname = window.location.pathname;

        // View Token
        const viewMatch = pathname.match(/^\/view\/([a-zA-Z0-9]+)$/);
        if (viewMatch) {
            setViewToken(viewMatch[1]);
        }

        // Content Paths
        if (pathname.startsWith('/use-cases/') ||
            pathname.startsWith('/blog') ||
            pathname.startsWith('/features/') ||
            pathname.startsWith('/compare/')) {
            setContentPath(pathname);
        }
    }, []);

    // Deep Linking & URL Sync - supports both slug and ID
    useEffect(() => {
        if (isLoading) return;

        const pathname = window.location.pathname;
        const slugMatch = pathname.match(/^\/board\/([a-z0-9-]+)$/i);
        const params = new URLSearchParams(window.location.search);
        const boardIdFromUrl = params.get('board');

        // Helper to handle specific board fetch
        const fetchBoard = async (slug: string) => {
            try {
                const res = await BoardService.findBoardBySlug(slug);
                if (res.success && res.data) {
                    const board = res.data;
                    setBoards(prev => {
                        if (prev.some(b => b.id === board.id)) return prev;
                        return [...prev, board];
                    });
                    setActiveBoardId(board.id);
                } else {
                    setBoardNotFound(true);
                }
            } catch (e) {
                console.error("Failed to fetch specific board for URL", e);
                setBoardNotFound(true);
            }
        };

        if (boards.length > 0) {
            if (slugMatch) {
                const slug = slugMatch[1].toLowerCase();
                const targetBoard = boards.find(b => b.slug === slug);
                if (targetBoard) {
                    setActiveBoardId(targetBoard.id);
                } else {
                    fetchBoard(slug);
                }
            } else if (boardIdFromUrl) {
                const targetBoard = boards.find(b => b.id === boardIdFromUrl);
                if (targetBoard) {
                    setActiveBoardId(boardIdFromUrl);
                    if (targetBoard.slug) {
                        window.history.replaceState({}, '', `/board/${targetBoard.slug}`);
                    }
                }
            }
        } else {
            // No boards loaded yet, check if we need to fetch specific one
            if (slugMatch) {
                const slug = slugMatch[1].toLowerCase();
                fetchBoard(slug);
            }
        }
        setIsRestoringUrl(false);
    }, [boards, isLoading, setBoards]);

    // Handle browser back/forward buttons (Popstate)
    useEffect(() => {
        const handlePopState = () => {
            const pathname = window.location.pathname;

            // Update Routes
            setIsHelpPage(pathname.startsWith('/help'));
            const articleMatch = pathname.match(/^\/help\/([a-z0-9-]+)$/i);
            setActiveHelpArticleId(articleMatch ? articleMatch[1] : null);
            setIsPrivacyPage(pathname === '/privacy');

            // Update Board Selection
            const slugMatch = pathname.match(/^\/board\/([a-z0-9-]+)$/i);

            if (pathname === '/' || !slugMatch) {
                if (!pathname.startsWith('/help') &&
                    !pathname.startsWith('/use-cases/') &&
                    !pathname.startsWith('/blog') &&
                    !pathname.startsWith('/features/') &&
                    !pathname.startsWith('/compare/')) {
                    setActiveBoardId(null);
                    setBoardNotFound(false);
                }
            } else if (slugMatch) {
                const slug = slugMatch[1].toLowerCase();
                const board = boards.find(b => b.slug === slug);
                if (board) {
                    setActiveBoardId(board.id);
                    setBoardNotFound(false);
                } else {
                    // Try fetch if missing
                    BoardService.findBoardBySlug(slug).then(res => {
                        if (res.success && res.data) {
                            const board = res.data;
                            setBoards(prev => prev.some(b => b.id === board.id) ? prev : [...prev, board]);
                            setActiveBoardId(board.id);
                            setBoardNotFound(false);
                        } else {
                            setBoardNotFound(true);
                        }
                    });
                }
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [boards, setBoards]);

    // Update URL when active board changes
    useEffect(() => {
        if (isRestoringUrl) return;

        if (activeBoardId) {
            const board = boards.find(b => b.id === activeBoardId);
            if (board?.slug) {
                window.history.pushState({}, '', `/board/${board.slug}`);
            } else {
                const url = new URL(window.location.href);
                url.pathname = '/';
                url.searchParams.set('board', activeBoardId);
                window.history.pushState({}, '', url.toString());
            }
        } else if (!isHelpPage && !isPrivacyPage && !contentPath && !viewToken) {
            // Only clear to '/' if we are not in another specific route
            window.history.pushState({}, '', '/');
        }
    }, [activeBoardId, boards, isRestoringUrl, isHelpPage, isPrivacyPage, contentPath, viewToken]);

    // Hydrate full board content
    useEffect(() => {
        if (!activeBoardId) return;
        let isMounted = true;

        const fetchBoardDetail = async () => {
            // Logic to fetch full board details if needed (lazy loading)
            // For now we assume if we have the board object we might need to refresh it or check if it's partial
            // Implemented in App.tsx originally
            try {
                const res = await BoardService.findBoardWithPages(activeBoardId);
                if (isMounted && res.success && res.data) {
                    setBoards(prev => prev.map(b => b.id === activeBoardId ? res.data! : b));
                }
            } catch (error) {
                console.error('Failed to hydrate board:', error);
            }
        };
        fetchBoardDetail();
        return () => { isMounted = false; };
    }, [activeBoardId]); // Missing setBoards dependency but it's stable usually. Added.

    // Return all routing state
    return {
        viewToken,
        contentPath,
        activeBoardId,
        setActiveBoardId,
        boardNotFound,
        setBoardNotFound,
        isHelpPage,
        setIsHelpPage,
        activeHelpArticleId,
        setActiveHelpArticleId,
        isPrivacyPage,
        setIsPrivacyPage,
        setContentPath
    };
}
